import axios from "axios";
import $api from "@/frontend/shared/api/axios";
import { Input } from "@/frontend/shared/ui/Input";
import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./ProfileSection.module.scss";
import { useEffect, useState } from "react";
import { Textarea } from "@/frontend/shared/ui/Textarea/Textarea";
import { NormalButton } from "@/frontend/shared/ui/Button";
import clsx from "clsx";
import { EditIcon } from "@/frontend/shared/assets/icons";
import { ProfilePictureModal } from "../ProfilePictureModal/ProfilePictureModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getProfileDetails } from "../../model/services/getProfileDetails";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { patchUpdateDetails } from "../../model/services/patchUpdateDetails";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { AxiosError } from "axios";
import { deleteProfilePhoto } from "../../model/services/deleteProfilePhoto";
import { stringValidation } from "@/frontend/shared/lib/stringValidation/stringValidation";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";

type Props = {
  handleApiCallFinally?: () => void;
};

export function ProfileSection({ handleApiCallFinally }: Props) {
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["profileData"],
    queryFn: async () => {
      const data = await getProfileDetails();
      setProfileData({
        name: data.firstName,
        surname: data.lastName,
        email: data.email,
        bio: data.bio,
        profileImageUrl: data.profileImageUrl
      });
      setName(data.firstName);
      setSurname(data.lastName);
      setEmail(data.email);
      setBio(data.bio);
      setOnUploadImage(data.profileImageUrl);
      return data;
    },
    refetchOnWindowFocus: false,
    throwOnError: (error) => {
      console.error(error);

      reportErrorToSentry(error, "SETTINGS: /me");
      return true;
    }
  });

  const [profileData, setProfileData] = useState({
    name: data?.firstName || "",
    surname: data?.lastName || "",
    email: data?.email || "",
    bio: data?.bio || "",
    profileImageUrl: data?.profileImageUrl || ""
  });
  const [name, setName] = useState(profileData?.name);
  const [surname, setSurname] = useState(profileData?.surname);
  const [email, setEmail] = useState(profileData?.email);
  const [bio, setBio] = useState(profileData?.bio);
  const [isEditing, setIsEditing] = useState(false);
  const [onUploadImage, setOnUploadImage] = useState(profileData?.profileImageUrl || null);
  const [onUploadFile, setOnUploadFile] = useState<File | null>(null);
  const [requestIsLoading, setRequestIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const [isShown, setIsShown] = useState({ name: false, email: false, bio: false });

  async function uploadImage() {
    if (!onUploadFile) {
      snackbarConstructor("Failed to load image. Try again later.", "error");
      return;
    }

    setIsImageLoading(true);

    const responseSasUrl = await $api.get<{ data: { url: string; token: string } }>("/me/profile-photo/url");
    const sasUrl = responseSasUrl?.data?.data?.url;
    const sasToken = responseSasUrl?.data?.data?.token;
    if (!sasUrl || !sasToken) {
      setIsImageLoading(false);
      snackbarConstructor("Failed to get response from server. Try again later.", "error");
      return;
    }

    const response = await axios.put(`${sasUrl}?${sasToken}`, onUploadFile, {
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": onUploadFile.type
      }
    });

    if (response.status === 201) {
      await $api.patch("/me/profile-photo/url", { profileImageUrl: sasUrl });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      handleApiCallFinally?.();
      setIsImageLoading(false);
      await refetch();
      setIsImageLoading(false);
    } else {
      setIsImageLoading(false);
      snackbarConstructor("Failed to upload image. Try again later.", "error");
    }
  }

  async function removeImage() {
    try {
      setIsImageLoading(true);
      await deleteProfilePhoto();
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      handleApiCallFinally?.();
      setIsImageLoading(false);
      setIsEditing(false);
      refetch();
    } catch (err) {
      const axiosError = err as AxiosError<{ message: string }>;
      const errorMessage = axiosError.response?.data?.message || "Unexpected error occurred";
      snackbarConstructor(errorMessage, "error");
    }
  }

  useEffect(() => {
    if (onUploadImage !== profileData.profileImageUrl && onUploadImage !== null) {
      uploadImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onUploadImage]);

  if (isLoading || isFetching)
    return (
      <div className={styles.loadingContainer}>
        <Loader />
      </div>
    );

  async function sendUpdatedDetailsData() {
    setRequestIsLoading(true);
    try {
      await patchUpdateDetails({
        firstName: name,
        lastName: surname,
        email: email,
        bio: bio,
        profileImageUrl: onUploadImage
      });
      refetch();
      setIsShown({ name: false, email: false, bio: false });
    } catch (err) {
      const axiosError = err as AxiosError<{ message: string }>;
      const errorMessage = axiosError.response?.data?.message || "Unexpected error occurred";
      snackbarConstructor(errorMessage, "error");
    } finally {
      setRequestIsLoading(false);
    }
  }

  return (
    <div className={styles.container}>
      <Typography className={styles.title} color="greyscale800" variant="heading-6">
        Profile
      </Typography>
      <div className={styles.contentContainer}>
        <div className={styles.imageContainer}>
          {profileData.profileImageUrl ? (
            <img
              className={styles.image}
              src={`${profileData.profileImageUrl}?${new Date().getTime()}`}
              alt="profile"
            />
          ) : (
            <div className={styles.image} />
          )}
          <div className={styles.editIconContainer} onClick={() => setIsEditing(!isEditing)}>
            <EditIcon />
          </div>
          <ProfilePictureModal
            isOpen={isEditing}
            onClose={() => setIsEditing(!isEditing)}
            onUpload={setOnUploadImage}
            onUploadFile={setOnUploadFile}
            onRemove={removeImage}
            image={profileData.profileImageUrl ? profileData.profileImageUrl : ""}
            isLoading={isImageLoading}
          />
        </div>
        <div className={styles.content}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <Typography variant="label-4" color="greyscale700" className={styles.nameTitle}>
                Name
              </Typography>
              {!isShown.name && (
                <Typography
                  onClick={() => setIsShown({ ...isShown, name: !isShown.name })}
                  className={styles.editBtn}
                  variant="label-5"
                >
                  Edit
                </Typography>
              )}
            </div>
            {isShown.name ? (
              <div className={styles.nameInputsContainer}>
                <div className={styles.nameInputsBlock}>
                  <Input
                    onChange={(e) => {
                      if (stringValidation(e.target.value)) setName(e.target.value);
                    }}
                    value={name}
                  />
                  <Input
                    onChange={(e) => {
                      if (stringValidation(e.target.value)) setSurname(e.target.value);
                    }}
                    value={surname}
                  />
                </div>
                <div className={styles.nameButtonsContainer}>
                  <NormalButton
                    size="medium"
                    onClick={() => {
                      setProfileData({ ...profileData, name, surname });
                      sendUpdatedDetailsData();
                    }}
                    isDisabled={requestIsLoading || name.length === 0 || surname.length === 0}
                  >
                    Save
                  </NormalButton>
                  <NormalButton
                    size="medium"
                    variant="tertiary"
                    color="greyscale600"
                    onClick={() => {
                      setName(profileData.name);
                      setSurname(profileData.surname);
                      setIsShown({ ...isShown, name: !isShown.name });
                    }}
                  >
                    Cancel
                  </NormalButton>
                </div>
              </div>
            ) : (
              <Typography variant="body-2" color="greyscale900">
                {profileData.name} {profileData.surname}
              </Typography>
            )}
          </div>
          <div className={clsx(styles.emailSection, !isShown.email && styles.emailResizedSection)}>
            <div className={styles.sectionTitle}>
              <Typography variant="label-4" color="greyscale700" className={styles.emailTitle}>
                Email
              </Typography>
              {/* {!isShown.email && (
                <Typography
                  onClick={() => setIsShown({ ...isShown, email: !isShown.email })}
                  className={styles.editBtn}
                  variant="label-5"
                >
                  Change Email
                </Typography>
              )} */}
            </div>
            {isShown.email ? (
              <div className={styles.emailInputContainer}>
                <Input
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  description="We’ll send you a verification link to verify your new email"
                />
                <Typography
                  className={styles.sendBtn}
                  onClick={() => {
                    sendUpdatedDetailsData();
                  }}
                  variant="label-4"
                >
                  Send
                </Typography>
              </div>
            ) : (
              <Typography variant="body-2" color="greyscale900">
                {email}
              </Typography>
            )}
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <Typography variant="label-4" color="greyscale700">
                Bio
              </Typography>
              <Typography
                onClick={() => setIsShown({ ...isShown, bio: !isShown.bio })}
                className={styles.editBtn}
                variant="label-5"
              >
                Edit
              </Typography>
            </div>
            {isShown.bio ? (
              <>
                <Textarea onChange={(e) => setBio(e.target.value)} value={bio} />
                <div className={styles.buttonsContainer}>
                  <NormalButton
                    size="medium"
                    onClick={() => {
                      setProfileData({ ...profileData, bio });
                      sendUpdatedDetailsData();
                    }}
                    isDisabled={requestIsLoading}
                  >
                    Save
                  </NormalButton>
                  <NormalButton
                    size="medium"
                    variant="tertiary"
                    color="greyscale600"
                    onClick={() => {
                      setBio(profileData.bio);
                      setIsShown({ ...isShown, bio: !isShown.bio });
                    }}
                  >
                    Cancel
                  </NormalButton>
                </div>
              </>
            ) : (
              <div className={styles.bioContainer}>
                <Typography variant="body-2" color="greyscale900" className={styles.bio}>
                  {bio}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
